import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Flex, Steps, Typography } from 'antd';
import { CheckOutlined, CloseOutlined, DownOutlined } from '@ant-design/icons';
import { TranslationLanguage } from '@lingoping/core';
import { DeckSession } from './DeckSession.js';
import { Echo } from './Echo.js';
import { TranslationLanguagePicker } from './TranslationLanguagePicker.js';
import { TranslationText } from './TranslationText.js';
import { StorageManager } from '../storage/index.js';
import { platform } from '../platform.js';
import { BASIC_GERMAN_DECK_ID, getCloudDecksApi } from '../services/cloudDecks.js';
import { deckLearnQueue } from '../services/deckDecisions.js';
import {
  getBaseTranslationLanguage,
  saveTranslationLanguage,
} from '../services/translationSettings.js';

const { Title, Paragraph, Text } = Typography;

interface Props {
  userId: string;
  onComplete: () => void;
  /** Payments are on and the learner is in their trial: say that trial emails will come. */
  trialNotice?: boolean;
}

/** The live example of the language step: "das Schiff" in each language (it needs no server). */
const EXAMPLE_WORD = { article: 'das', lemma: 'Schiff' };
const EXAMPLE_TRANSLATIONS: Record<TranslationLanguage, string> = {
  en: 'ship',
  ar: 'سفينة',
  ru: 'корабль',
  tr: 'gemi',
  uk: 'корабель',
  es: 'barco',
  fa: 'کشتی',
  pl: 'statek',
  ro: 'navă',
};

/**
 * First-run experience: choose the translation language, then the public deck "Basic German
 * Vocabulary" is followed for the new learner and they go through its first words, choosing for
 * each one: Learn it, I know it or Not now.
 */
export const OnboardingView: React.FC<Props> = ({ userId, onComplete, trialNotice }) => {
  const [step, setStep] = useState<'language' | 'intro' | 'deck' | 'done'>('language');
  const [language, setLanguage] = useState<TranslationLanguage>('en');
  const [saving, setSaving] = useState(false);
  const [deckState, setDeckState] = useState<{
    status: 'loading' | 'ready' | 'failed';
    count?: number;
  }>({
    status: 'loading',
  });

  // Starts on whatever is already set (English, or what another device chose).
  useEffect(() => {
    void getBaseTranslationLanguage().then(setLanguage);
  }, []);

  const finish = async () => {
    await StorageManager.completeOnboarding();
    // Where reviews run is not this page's business: the platform may add a last step for it.
    // Only for a learner who chose words: with nothing to review yet, "start reviewing" would
    // lead nowhere, so they go straight to the dashboard.
    const hasWords =
      deckLearnQueue.active().length > 0 || (await StorageManager.getCards()).length > 0;
    if (platform.AfterOnboarding && hasWords) setStep('done');
    else onComplete();
  };

  // Basic German is followed for every new learner (kept offline and uploaded later if needed),
  // and its words are loaded now so the first card appears without a wait.
  const prepareDeck = async () => {
    setDeckState({ status: 'loading' });
    try {
      const api = await getCloudDecksApi();
      await api.refreshLists();
      let open = await api.refreshDeck(BASIC_GERMAN_DECK_ID, { force: true });
      if (!open || open.words.length === 0) open = await api.readDeck(BASIC_GERMAN_DECK_ID);
      if (!open || open.words.length === 0) {
        setDeckState({ status: 'failed' });
        return;
      }
      setDeckState({ status: 'ready', count: open.words.length });
    } catch (err) {
      console.error('Could not load Basic German Vocabulary:', err);
      setDeckState({ status: 'failed' });
    }
  };

  const continueFromLanguage = async () => {
    setSaving(true);
    try {
      await saveTranslationLanguage(language);
    } catch (err) {
      console.error('Could not save the translation language:', err);
    }
    try {
      await (await getCloudDecksApi()).follow(BASIC_GERMAN_DECK_ID);
    } catch (err) {
      console.error('Could not follow Basic German Vocabulary:', err);
    } finally {
      setSaving(false);
    }
    setStep('intro');
    void prepareDeck();
  };

  if (step === 'language') {
    const exampleText = EXAMPLE_TRANSLATIONS[language] ?? EXAMPLE_TRANSLATIONS.en;
    return (
      <Card style={{ maxWidth: 600, margin: '32px auto' }}>
        <Flex vertical gap={18}>
          <Flex align="center" gap={16}>
            <Echo mood="hello" size={88} />
            <div>
              <Title level={3} style={{ margin: 0 }}>
                Welcome to LingoPing
              </Title>
              <Text style={{ color: '#475569' }}>
                I'm Echo. First, which language should I translate German words into?
              </Text>
            </div>
          </Flex>
          <TranslationLanguagePicker value={language} onChange={setLanguage} />
          {exampleText && (
            <Flex
              align="center"
              gap={10}
              wrap
              aria-live="polite"
              style={{
                padding: '12px 14px',
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: 10,
              }}
            >
              <Text style={{ fontSize: 13, color: '#475569' }}>Example:</Text>
              <Text strong style={{ fontSize: 15 }}>
                <span style={{ color: '#15803d' }}>{EXAMPLE_WORD.article}</span>{' '}
                {EXAMPLE_WORD.lemma}
              </Text>
              <span aria-hidden="true" style={{ color: '#94a3b8' }}>
                →
              </span>
              <TranslationText
                as="span"
                text={exampleText}
                lang={language}
                style={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}
              />
            </Flex>
          )}
          <Flex align="center" justify="space-between" gap={12} wrap>
            <Text style={{ fontSize: 13, color: '#475569' }}>You can change this later.</Text>
            <Button type="primary" size="large" loading={saving} onClick={continueFromLanguage}>
              Continue
            </Button>
          </Flex>
          {trialNotice && (
            <Text style={{ fontSize: 13, color: '#475569' }}>
              We&apos;ll email you about your trial. You can turn that off in Settings.
            </Text>
          )}
        </Flex>
      </Card>
    );
  }

  if (step === 'intro') {
    return (
      <Card style={{ maxWidth: 560, margin: '40px auto' }}>
        <Flex align="center" gap={16} style={{ marginBottom: 12 }}>
          <Echo mood="hello" size={96} />
          <div>
            <Title level={3} style={{ margin: 0 }}>
              Let's build your first cards
            </Title>
            <Text style={{ color: '#475569' }}>I'll ping you when it's time to review.</Text>
          </div>
        </Flex>
        <Paragraph style={{ color: '#475569' }}>
          You&apos;re now following <strong>Basic German Vocabulary</strong>
          {deckState.count ? `, ${deckState.count} of the most common German words` : ''}. I&apos;ll
          show you one word at a time. For each, choose:
        </Paragraph>
        <Steps
          orientation="vertical"
          size="small"
          current={-1}
          items={[
            {
              title: 'Learn it: swipe right or tap the check',
              content: 'It becomes a card.',
              icon: <CheckOutlined style={{ color: '#7c3aed' }} />,
            },
            {
              title: 'I know it: swipe left or tap the cross',
              content: "It won't show up again.",
              icon: <CloseOutlined style={{ color: '#b45309' }} />,
            },
            {
              title: 'Not now: swipe down',
              content: 'It comes back later.',
              icon: <DownOutlined style={{ color: '#475569' }} />,
            },
          ]}
        />
        <Text style={{ display: 'block', margin: '12px 0', color: '#475569' }}>
          You can also use the arrow keys, and Backspace to undo.
        </Text>
        {deckState.status === 'failed' && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            title="The words couldn't be loaded"
            description="Check your connection and try again, or add words yourself."
            action={
              <Button size="small" onClick={() => void prepareDeck()}>
                Try again
              </Button>
            }
          />
        )}
        <Flex gap={8} wrap>
          <Button
            type="primary"
            size="large"
            loading={deckState.status === 'loading'}
            disabled={deckState.status === 'failed'}
            onClick={() => setStep('deck')}
          >
            Start
          </Button>
          <Button type="text" onClick={finish}>
            I'll add words myself
          </Button>
        </Flex>
      </Card>
    );
  }

  if (step === 'done' && platform.AfterOnboarding) {
    const { AfterOnboarding } = platform;
    return (
      <Card style={{ maxWidth: 560, margin: '40px auto' }}>
        <Flex align="center" gap={16} style={{ marginBottom: 12 }}>
          <Echo mood="celebrate" size={96} />
          <div>
            <Title level={3} style={{ margin: 0 }}>
              You're all set
            </Title>
            <Text style={{ color: '#475569' }}>Your words are saved to your account.</Text>
          </div>
        </Flex>
        <AfterOnboarding />
        <Button type="primary" size="large" onClick={onComplete} style={{ marginTop: 16 }}>
          Go to my dashboard
        </Button>
      </Card>
    );
  }

  return (
    <DeckSession
      deckId={BASIC_GERMAN_DECK_ID}
      userId={userId}
      exitLabel="Start learning"
      onExit={finish}
      stopLabel="That's enough for now"
    />
  );
};
